import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, Stethoscope, ListChecks } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { WaitlistForm } from "@/components/ankle/AnkleResult";

const PATHWAYS = [
  "Mid-portion Achilles", "Insertional Achilles / retrocalcaneal", "Posterior ankle impingement",
  "Lateral ankle sprain / instability", "Peroneal tendinopathy", "Tibialis posterior", "Plantar heel pain", "and more",
];

export default function AnkleProgramme() {
  return (
    <div className="fl-page min-h-screen" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />
      <section className="pt-32 md:pt-40 pb-16 px-6 bg-[#0A0A0A] fl-noise">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1.2fr_1fr] gap-10 items-start">
          <div>
            <Link to="/prices#online-programmes" className="text-xs text-[#f47a20] hover:underline" data-testid="ankle-back-prices">← Online programmes</Link>
            <div className="fl-label text-[#f47a20] mt-4">Ankle · self-guided rehab</div>
            <h1 className="fl-heading text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="ankle-heading">Find the right ankle pathway in five minutes.</h1>
            <p className="text-sm md:text-base mt-4 max-w-xl text-zinc-400">
              Answer a short, branching questionnaire — safety first, then only the questions that apply to your ankle.
              You'll get a clear next step: personalised support now, and early access to our upcoming self-guided ankle programmes.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/online-programmes/ankle/finder" className="fl-btn text-base justify-center" data-testid="ankle-start-finder">Start the Ankle Programme Finder <ArrowRight size={16} /></Link>
              <Link to="/contact" className="fl-btn-ghost text-base justify-center" data-testid="ankle-consult">Request an initial consultation</Link>
            </div>
            <div className="mt-4 text-xs text-zinc-500">No account needed. Not a medical diagnosis.</div>
          </div>
          <div className="fl-card p-6 space-y-4" data-testid="ankle-offer-card">
            <div className="fl-label text-[#f47a20]">What you get today</div>
            {[
              [ShieldCheck, "Safety screen with clear guidance if something needs urgent or clinical attention."],
              [ListChecks, "A pathway match — which of our ankle rehabilitation pathways your answers fit best."],
              [Stethoscope, "Personalised support: request an initial consultation with a Functional LAB clinician."],
              [Clock, "Early access: join the waiting list for launch updates on the self-guided programmes."],
            ].map(([Icon, text]) => (
              <div key={text} className="flex items-start gap-3 text-sm text-zinc-300"><Icon size={16} className="text-[#f47a20] mt-0.5 shrink-0" /> {text}</div>
            ))}
            <div className="text-[11px] text-zinc-500 pt-2 border-t border-white/10">Our self-guided ankle programmes are in development and are not yet available to buy. Nothing is charged for using the finder.</div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
          <div>
            <div className="fl-label text-[#f47a20]">Pathways we're building</div>
            <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-zinc-300" data-testid="ankle-pathway-list">
              {PATHWAYS.map((p) => <li key={p} className="rounded-md border border-white/10 px-3 py-2">{p}</li>)}
            </ul>
            <p className="text-xs text-zinc-500 mt-3">Your questionnaire answers help us prioritise which programme to release first.</p>
          </div>
          <div className="fl-card p-6" data-testid="ankle-waitlist-card">
            <div className="fl-label text-[#f47a20]">Waiting list</div>
            <div className="fl-heading text-xl mt-1">Be first to know at launch</div>
            <p className="text-xs text-zinc-400 mt-2">Launch links bring you back to the programme finder so you can check suitability — nothing is charged or enrolled automatically.</p>
            <WaitlistForm pathway="GENERAL" />
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
